import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Drop, Plus, Trash, Warning } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { GLYCEMIA_TYPES, GLYCEMIA_TYPE_LABELS, limitFor, isOverLimit, computeWeeklyWithinRange } from '@/lib/glycemia';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

export default function GlicemiaPage() {
  const confirmDelete = useConfirmDelete();
  const [data, setData] = useState({ records: [], limits: {}, enabled: false });
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const now = new Date();
  const defForm = () => ({ date: now.toISOString().split('T')[0], time: now.toTimeString().slice(0, 5), reading_type: 'jejum', value: '', notes: '' });
  const [form, setForm] = useState(defForm());

  const fetchData = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/my-glycemia`); setData(data || { records: [], limits: {}, enabled: false }); } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async () => {
    if (form.value === '') { toast.error('Indica o valor'); return; }
    try {
      await axios.post(`${API}/api/my-glycemia`, { ...form, value: parseFloat(form.value) });
      toast.success('Leitura guardada');
      setShowAdd(false); setForm(defForm()); fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao guardar'); }
  };
  const del = async (id) => {
    const rec = data.records.find(r => r.id === id);
    const ok = await confirmDelete({ description: `Vai apagar a leitura de glicemia de ${rec?.date || ''}${rec?.value != null ? ` (${rec.value} mg/dL)` : ''}.` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/my-glycemia/${id}`); toast.success('Leitura eliminada'); fetchData(); } catch { /* noop */ }
  };

  const weekly = computeWeeklyWithinRange(data.records, data.limits);
  const currentWeek = weekly.length ? weekly[weekly.length - 1] : null;
  const hasAnyLimit = GLYCEMIA_TYPES.some(t => limitFor(data.limits, t) !== null);

  if (loading) return <div className="text-center py-16 text-brand-muted" data-testid="glicemia-page">A carregar...</div>;

  if (!data.enabled) {
    return (
      <div className="fade-in" data-testid="glicemia-page">
        <h2 className="font-heading font-medium text-xl text-brand-text mb-4" data-testid="glicemia-title">Glicemia</h2>
        <div className="text-center py-16" data-testid="glicemia-disabled">
          <Drop size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" />
          <p className="text-brand-muted">O módulo de glicemia não está ativo para a tua conta.</p>
          <p className="text-xs text-brand-placeholder mt-1">Fala com a tua nutricionista para o ativar.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in" data-testid="glicemia-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text" data-testid="glicemia-title">Glicemia</h2>
          <p className="text-sm text-brand-muted mt-1">Regista as tuas medições capilares</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="bg-brand-green text-white rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="add-glycemia-btn">
          <Plus size={16} /> Nova leitura
        </button>
      </div>

      {hasAnyLimit && (
        <div className="bg-white border border-brand-border rounded-3xl p-5 mb-5" data-testid="glicemia-limits">
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-3">Limites definidos pela tua nutricionista</h3>
          <div className="grid grid-cols-2 gap-2">
            {GLYCEMIA_TYPES.map(t => { const lim = limitFor(data.limits, t); return (
              <div key={t} className="flex items-center justify-between text-xs bg-brand-bg/40 rounded-xl px-3 py-2">
                <span className="text-brand-text">{GLYCEMIA_TYPE_LABELS[t]}</span>
                <span className="text-brand-muted">{lim !== null ? `≤ ${lim} mg/dL` : '-'}</span>
              </div>
            ); })}
          </div>
        </div>
      )}

      {currentWeek && (
        <div className="bg-white border border-brand-border rounded-3xl p-5 mb-5 shadow-[0_8px_32px_rgba(101,113,97,0.04)]" data-testid="glicemia-within-range">
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-1">% de leituras dentro do intervalo</h3>
          <p className="text-[10px] text-brand-placeholder mb-3">Só conta leituras cujo tipo tem limite definido</p>
          <div className="space-y-1.5">
            {weekly.slice().reverse().map(b => (
              <div key={b.week} className="flex items-center justify-between text-xs" data-testid={`glicemia-week-${b.week}`}>
                <span className="text-brand-text">Semana {b.label}</span>
                <span className={`font-semibold ${b.pct >= 70 ? 'text-brand-green' : b.pct >= 40 ? 'text-brand-gold-hover' : 'text-red-500'}`}>{b.pct}% <span className="text-[10px] font-normal text-brand-muted">({b.within}/{b.total})</span></span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.records.length === 0 ? (
        <div className="text-center py-16">
          <Drop size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" />
          <p className="text-brand-muted">Sem leituras registadas.</p>
        </div>
      ) : (
        <div>
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-3">Histórico</h3>
          <div className="space-y-2">
            {data.records.map(r => { const over = isOverLimit(r, data.limits); return (
              <div key={r.id} className={`border rounded-2xl p-3 flex items-center justify-between ${over ? 'bg-red-50 border-red-200' : 'bg-white border-brand-border'}`} data-testid={`glycemia-record-${r.id}`}>
                <div>
                  <span className="text-xs text-brand-muted">{fmtDatePt(r.date)} · {r.time}</span>
                  <p className="text-xs text-brand-text mt-0.5">{GLYCEMIA_TYPE_LABELS[r.reading_type] || r.reading_type}</p>
                  {r.notes && <p className="text-[10px] text-brand-placeholder mt-0.5">{r.notes}</p>}
                </div>
                <div className="flex items-center gap-3">
                  {over && <span className="flex items-center gap-1 text-[10px] text-red-500 font-medium" data-testid={`glycemia-flag-${r.id}`}><Warning size={13} weight="fill" /> Acima do limite</span>}
                  <p className={`text-sm font-heading font-semibold ${over ? 'text-red-500' : 'text-brand-text'}`}>{r.value} mg/dL</p>
                  <button onClick={() => del(r.id)} className="p-1 text-brand-placeholder hover:text-red-500" data-testid={`delete-glycemia-${r.id}`}><Trash size={13} /></button>
                </div>
              </div>
            ); })}
          </div>
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Nova leitura de glicemia</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs text-brand-muted mb-1 block">Data</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-xl border-brand-border" data-testid="glycemia-date" /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Hora</Label><Input type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} className="rounded-xl border-brand-border" data-testid="glycemia-time" /></div>
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Momento</Label>
              <select value={form.reading_type} onChange={e => setForm({ ...form, reading_type: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="glycemia-type">
                {GLYCEMIA_TYPES.map(t => <option key={t} value={t}>{GLYCEMIA_TYPE_LABELS[t]}</option>)}
              </select>
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Valor (mg/dL)</Label><Input type="number" step="1" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} placeholder="Ex: 95" className="rounded-xl border-brand-border" data-testid="glycemia-value" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Notas (opcional)</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="rounded-xl border-brand-border" data-testid="glycemia-notes" /></div>
            <button onClick={handleAdd} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="save-glycemia-btn">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
